import { type FormEvent, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ErrorBanner, Loading } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMe, useRequestCode, useVerifyCode } from "../api/session";
import { isStandalone } from "../pwa/install";
import { OfflineScreen } from "../pwa/OfflineScreen";
import { isNetworkError } from "../pwa/online";

const NO_CONNECTION = new Error("Sem conexão com a internet.");
/** Sem rede (antes de enviar ou sem resposta): uma mensagem só, em vez do texto técnico. */
const loginError = (error: unknown) => (isNetworkError(error) ? NO_CONNECTION : error);

/** Só aceita destinos internos do app. */
export function safeNext(value: string | null): string {
  if (!value?.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  return value;
}

export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const navigate = useNavigate();
  const me = useMe();
  const requestCode = useRequestCode();
  const verify = useVerifyCode();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  // App instalado (iPhone): cookies separados do Safari, então o link do e-mail entra no navegador, não aqui.
  const [standalone] = useState(isStandalone);
  const codeInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (standalone && step === "code") codeInput.current?.focus();
  }, [standalone, step]);
  const installedNote = standalone && (
    <p className="m-0 rounded-control bg-accent-soft px-3 py-2 text-sm text-accent-soft-ink">
      No app, entre pelo código enviado por e-mail. O link do e-mail abre no navegador.
    </p>
  );

  if (me.isPending && me.fetchStatus === "paused") return <OfflineScreen onRetry={() => void me.refetch()} />;
  if (me.isPending) return <Loading />;
  if (me.data) return <Navigate to={next} replace />;

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    await requestCode.mutateAsync(email.trim());
    setStep("code");
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    await verify.mutateAsync({ email: email.trim(), code: code.trim() });
    navigate(next, { replace: true });
  }

  return (
    <main className="flex min-h-dvh flex-col bg-bg text-ink lg:flex-row">
      <section
        aria-label="Nora"
        className="relative overflow-hidden bg-accent px-6 pt-10 pb-14 text-accent-ink lg:flex lg:w-[44%] lg:flex-col lg:justify-between lg:p-14"
      >
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-20 size-80 rounded-full border-[56px] border-accent-ink/10"
        />
        <Logo onAccent className="relative" />
        <div className="relative mt-8 max-w-md lg:mt-0">
          <p className="text-3xl leading-tight font-bold tracking-[-0.03em] lg:text-5xl">
            Seu dinheiro, claro como um extrato.
          </p>
          <p className="mt-3 text-base text-accent-ink/85 lg:text-lg">
            Saldos, gastos do mês e orçamentos num só lugar — e um agente que lança por você.
          </p>
        </div>
      </section>

      <div className="-mt-6 flex flex-1 items-start justify-center px-4 pb-10 lg:mt-0 lg:items-center lg:px-14">
        <div className="relative z-10 w-full max-w-sm rounded-card bg-surface p-6 shadow-card lg:bg-transparent lg:p-0 lg:shadow-none">
          <h1 className="text-2xl font-bold tracking-[-0.02em]">Entrar</h1>
          {step === "email" ? (
            <form
              onSubmit={(e) => void sendCode(e).catch(() => undefined)}
              className="mt-6 flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1 text-sm text-muted">
                <p>Sem senha: enviamos um código de acesso para o seu e-mail.</p>
                <p>O Nora está em acesso por convite.</p>
              </div>
              {installedNote}
              <div className="flex flex-col gap-2">
                <Label htmlFor="login-email">E-mail</Label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <ErrorBanner error={loginError(requestCode.error)} />
              <Button type="submit" size="lg" disabled={requestCode.isPending}>
                Enviar código
              </Button>
            </form>
          ) : (
            <form
              onSubmit={(e) => void submitCode(e).catch(() => undefined)}
              className="mt-6 flex flex-col gap-4"
            >
              <p className="text-sm text-muted">
                Se {email} tiver acesso ao Nora, você vai receber um código em instantes. Ele vale por 10
                minutos{standalone ? "." : " — você também pode entrar pelo link do e-mail."}
              </p>
              {installedNote}
              <div className="flex flex-col gap-2">
                <Label htmlFor="login-code">Código</Label>
                <Input
                  id="login-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  ref={codeInput}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="text-center text-2xl font-semibold tracking-[0.4em] tabular-nums"
                />
              </div>
              <ErrorBanner error={loginError(verify.error)} />
              <Button type="submit" size="lg" disabled={verify.isPending}>
                Entrar
              </Button>
              <Button variant="ghost" onClick={() => setStep("email")}>
                Usar outro e-mail
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
